import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize, forkJoin } from 'rxjs';
import { HotspotAssetMapping, HotspotSessionRemapResponse } from '../../core/models/hotspot-asset-mapping.model';
import { Organisation } from '../../core/models/organisation.model';
import { Vehicle } from '../../core/models/vehicle.model';
import { HotspotAssetMappingService } from '../../core/services/hotspot-asset-mapping.service';
import { OrganisationService } from '../../core/services/organisation.service';
import { VehicleService } from '../../core/services/vehicle.service';

@Component({
  selector: 'app-hotspot-asset-mappings',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './hotspot-asset-mappings.component.html',
  styleUrls: ['./hotspot-asset-mappings.component.css']
})
export class HotspotAssetMappingsComponent implements OnInit {
  private assetMappingService = inject(HotspotAssetMappingService);
  private organisationService = inject(OrganisationService);
  private vehicleService = inject(VehicleService);

  mappings: HotspotAssetMapping[] = [];
  selectedMapping: HotspotAssetMapping | null = null;
  organisations: Organisation[] = [];
  vehicles: Vehicle[] = [];

  searchTerm = '';
  providerFilter = '';
  unresolvedOnly = false;
  unverifiedOnly = false;
  page = 1;
  perPage = 25;
  total = 0;
  totalPages = 1;

  editOrganisationID: number | null = null;
  editVehicleID: number | null = null;
  editIsVerified = false;
  remapLimit = 1000;
  remapResult: HotspotSessionRemapResponse | null = null;

  isLoading = false;
  isLoadingDetail = false;
  isSaving = false;
  isRemapping = false;
  errorMessage = '';
  detailError = '';
  remapError = '';
  successMessage = '';
  readonly remapLimitOptions = [1000, 2500, 5000];

  ngOnInit(): void {
    this.loadReferenceData();
    this.loadMappings();
  }

  loadReferenceData(): void {
    forkJoin({
      organisations: this.organisationService.getOrganisations(),
      vehicles: this.vehicleService.getVehicles()
    }).subscribe({
      next: ({ organisations, vehicles }) => {
        this.organisations = organisations;
        this.vehicles = vehicles;
      },
      error: error => {
        console.error('Unable to load mapping reference data:', error);
        this.errorMessage = 'Unable to load organisations and vehicles for mapping correction.';
      }
    });
  }

  loadMappings(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.assetMappingService.getAssetMappings({
      page: this.page,
      perPage: this.perPage,
      unresolved: this.unresolvedOnly || undefined,
      verified: this.unverifiedOnly ? false : undefined,
      provider: this.providerFilter,
      search: this.searchTerm
    }).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe({
      next: response => {
        this.mappings = response.data;
        this.page = response.meta.page;
        this.perPage = response.meta.perPage;
        this.total = response.meta.total;
        this.totalPages = response.meta.totalPages;
      },
      error: error => {
        console.error('Unable to load hotspot asset mappings:', error);
        this.mappings = [];
        this.total = 0;
        this.totalPages = 1;
        this.errorMessage = error?.error?.message || error?.message || 'Unable to load hotspot asset mappings.';
      }
    });
  }

  applyFilters(): void {
    this.page = 1;
    this.loadMappings();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.providerFilter = '';
    this.unresolvedOnly = false;
    this.unverifiedOnly = false;
    this.applyFilters();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.page) {
      return;
    }

    this.page = page;
    this.loadMappings();
  }

  selectMapping(mapping: HotspotAssetMapping): void {
    this.isLoadingDetail = true;
    this.detailError = '';
    this.successMessage = '';

    this.assetMappingService.getAssetMapping(mapping.hotspotAssetMappingID).pipe(
      finalize(() => this.isLoadingDetail = false)
    ).subscribe({
      next: detail => {
        this.selectedMapping = detail;
        this.resetEditForm(detail);
      },
      error: error => {
        console.error('Unable to load asset mapping detail:', error);
        this.detailError = error?.error?.message || error?.message || 'Unable to load mapping detail.';
      }
    });
  }

  saveMapping(): void {
    if (!this.selectedMapping) {
      return;
    }

    this.isSaving = true;
    this.detailError = '';
    this.successMessage = '';

    this.assetMappingService.updateAssetMapping(this.selectedMapping.hotspotAssetMappingID, {
      organisationID: this.editOrganisationID,
      vehicleID: this.editVehicleID,
      isVerified: this.editIsVerified
    }).pipe(
      finalize(() => this.isSaving = false)
    ).subscribe({
      next: updated => {
        this.selectedMapping = updated;
        this.resetEditForm(updated);
        this.successMessage = 'Hotspot asset mapping updated.';
        this.loadMappings();
      },
      error: error => {
        console.error('Unable to save asset mapping:', error);
        this.detailError = error?.error?.message || error?.message || 'Unable to save hotspot asset mapping.';
      }
    });
  }

  remapExistingSessions(): void {
    this.isRemapping = true;
    this.remapError = '';
    this.remapResult = null;
    this.successMessage = '';

    this.assetMappingService.remapExistingSessions(Number(this.remapLimit) || 1000).pipe(
      finalize(() => this.isRemapping = false)
    ).subscribe({
      next: result => {
        this.remapResult = result;
        this.successMessage = `Reviewed ${result.reviewed} sessions and remapped ${result.remappedSessions}.`;
        this.loadMappings();
      },
      error: error => {
        console.error('Unable to remap existing hotspot sessions:', error);
        this.remapError = error?.error?.message || error?.message || 'Unable to remap existing hotspot sessions.';
      }
    });
  }

  getOrganisationName(id?: number | null): string {
    if (!id) {
      return 'Unresolved organisation';
    }

    const organisation = this.organisations.find(item => item.organisationID === Number(id));
    return organisation?.organisationName || organisation?.orgName || `Organisation ${id}`;
  }

  getOrganisationOperatorCode(id?: number | null): string {
    if (!id) {
      return '';
    }

    const organisation = this.organisations.find(item => item.organisationID === Number(id));
    return organisation?.operatorCode?.trim() || '';
  }

  getOrganisationPowerlynxLocationID(id?: number | null): string {
    if (!id) {
      return '';
    }

    const organisation = this.organisations.find(item => item.organisationID === Number(id));
    const value = organisation?.powerlynxLocationID ?? organisation?.powerlynxLocationId;
    return value ? String(value) : '';
  }

  getOrganisationOptionLabel(organisation: Organisation): string {
    const name = organisation.organisationName || organisation.orgName || `Organisation ${organisation.organisationID}`;
    const powerlynxLocationID = organisation.powerlynxLocationID ?? organisation.powerlynxLocationId;
    return powerlynxLocationID
      ? `${name} (Powerlynx location ${powerlynxLocationID})`
      : name;
  }

  getVehicleLabel(vehicle?: Vehicle | null): string {
    if (!vehicle) {
      return 'Unresolved bus';
    }

    return [
      vehicle.vehicleIdentificationNumber || `Vehicle ${vehicle.vehicleID}`,
      vehicle.vehicleRegistration,
      vehicle.vehicleLabel
    ].filter(Boolean).join(' / ');
  }

  getSelectedVehicle(): Vehicle | null {
    return this.vehicles.find(vehicle => vehicle.vehicleID === Number(this.editVehicleID)) || null;
  }

  onOrganisationSelected(): void {
    if (!this.editOrganisationID || !this.editVehicleID) {
      return;
    }

    const selectedVehicle = this.getSelectedVehicle();
    if (selectedVehicle?.organisationID !== Number(this.editOrganisationID)) {
      this.editVehicleID = null;
    }
  }

  onVehicleSelected(): void {
    const vehicle = this.getSelectedVehicle();
    if (vehicle?.organisationID) {
      this.editOrganisationID = vehicle.organisationID;
    }
  }

  getFilteredVehicles(): Vehicle[] {
    return this.editOrganisationID
      ? this.vehicles.filter(vehicle => vehicle.organisationID === Number(this.editOrganisationID))
      : this.vehicles;
  }

  isUnresolved(mapping: HotspotAssetMapping): boolean {
    return !mapping.organisationID || !mapping.vehicleID;
  }

  getRouterIdentityLabel(mapping: HotspotAssetMapping): string {
    return mapping.nasIdentifier?.trim() || 'Unknown router identity';
  }

  isDefaultRouterIdentity(mapping: HotspotAssetMapping): boolean {
    return mapping.nasIdentifier?.trim().toLowerCase() === 'mikrotik';
  }

  formatExternalId(value?: number | string | null): string {
    return value === null || value === undefined || value === '' ? '-' : String(value);
  }

  hasActiveFilters(): boolean {
    return Boolean(this.searchTerm.trim() || this.providerFilter.trim() || this.unresolvedOnly || this.unverifiedOnly);
  }

  getPageSummary(): string {
    if (this.total === 0) {
      return '0 mappings';
    }

    const start = ((this.page - 1) * this.perPage) + 1;
    const end = Math.min(this.page * this.perPage, this.total);
    return `${start}-${end} of ${this.total}`;
  }

  formatDate(value?: string | null): string {
    if (!value) {
      return '-';
    }

    return new Intl.DateTimeFormat('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(value));
  }

  trackMapping(_index: number, mapping: HotspotAssetMapping): number {
    return mapping.hotspotAssetMappingID;
  }

  trackVehicle(_index: number, vehicle: Vehicle): number {
    return vehicle.vehicleID;
  }

  private resetEditForm(mapping: HotspotAssetMapping): void {
    this.editOrganisationID = mapping.organisationID ?? null;
    this.editVehicleID = mapping.vehicleID ?? null;
    this.editIsVerified = mapping.isVerified;
  }
}
